import { useSelector } from 'react-redux';
import type { LabelType } from '@common/customControls/getServiceLabel';
import type { IReduxState } from '../../../configureStore';
import type {
    IDiagnosticReportGetOne,
    IProcedureGetOne,
    IRequestGetOne,
    ISupervisionGetOne,
    ITask,
} from '../../interfaces/';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { ButtonContainer } from '../common';
import {
    BecomeRequesterButton,
    CancelProcedureButton,
    ConfirmDiagnosticReportButton,
    ConvertToConsultButton,
    ConvertToReferralButton,
    EditProcedureButton,
    PrintCarePathButton,
    RemoveProcedureButton,
    TakeDiagnosticReportButton,
    TakeProcedureButton,
    TakeProcedureDiagnosticReportButton,
    TakeSupervisionButton,
    UpdateSupervisionButton,
} from './';

interface IProcedureDiagnosticReportReadButtonsProps {
    acceptDiagnosticReport: () => Promise<void>;
    diagnosticReport: IDiagnosticReportGetOne;
    editProcedure: () => Promise<void>;
    hideConvertToConsultButton?: boolean;
    hidePrintButton?: boolean;
    isLastItem: boolean;
    isPreviousRequest?: boolean;
    procedure: IProcedureGetOne;
    reloadProcess: () => Promise<void>;
    removeProcedure: () => Promise<void>;
    request: IRequestGetOne;
    supervision?: ISupervisionGetOne;
    task?: ITask;
    toastServiceLabelType?: LabelType;
    updateSupervision?: () => Promise<void>;
}

export function ProcedureDiagnosticReportReadButtons(props: IProcedureDiagnosticReportReadButtonsProps) {
    const {
        request,
        procedure,
        supervision,
        task,
        diagnosticReport,
        isLastItem,
        isPreviousRequest,
        toastServiceLabelType,
        hideConvertToConsultButton,
        hidePrintButton,
        editProcedure,
        reloadProcess,
        removeProcedure,
        updateSupervision,
        acceptDiagnosticReport,
    } = props;
    const { patient, onConvertToConsult, onConvertToReferral } = useEpisodeContext();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);

    if (!isLastItem || isManager) {
        return null;
    }

    return (
        <ButtonContainer
            alignRight
            buttons={[
                <PrintCarePathButton
                    hidePrintButton={hidePrintButton}
                    isLastItem={isLastItem}
                    isPreviousRequest={isPreviousRequest}
                    key="PrintCarePathButton"
                    request={request}
                />,
                <CancelProcedureButton
                    key="CancelProcedureButton"
                    procedure={procedure}
                    removeProcedure={removeProcedure}
                    serviceCode={request.service.code}
                    toastServiceLabelType={toastServiceLabelType}
                />,
                <RemoveProcedureButton
                    key="RemoveProcedureButton"
                    procedure={procedure}
                    removeProcedure={removeProcedure}
                    serviceCode={request.service.code}
                />,
                <EditProcedureButton
                    editProcedure={editProcedure}
                    key="EditProcedureButton"
                    procedure={procedure}
                />,
                <TakeProcedureButton
                    diagnosticReport={diagnosticReport}
                    key="TakeProcedureButton"
                    procedure={procedure}
                    reloadProcess={reloadProcess}
                    request={request}
                />,
                <BecomeRequesterButton
                    key="BecomeRequesterButton"
                    procedure={procedure}
                    reloadProcess={reloadProcess}
                    request={request}
                />,
                <TakeDiagnosticReportButton
                    diagnosticReport={diagnosticReport}
                    key="TakeDiagnosticReportButton"
                    procedure={procedure}
                    reloadProcess={reloadProcess}
                />,
                <TakeProcedureDiagnosticReportButton
                    diagnosticReport={diagnosticReport}
                    key="TakeProcedureDiagnosticReportButton"
                    procedure={procedure}
                    reloadProcess={reloadProcess}
                    request={request}
                />,
                <ConvertToConsultButton
                    acceptDiagnosticReport={acceptDiagnosticReport}
                    diagnosticReport={diagnosticReport}
                    hideConvertToConsultButton={hideConvertToConsultButton}
                    isPreviousRequest={isPreviousRequest}
                    key="ConvertToConsultButton"
                    onConvertToConsult={onConvertToConsult}
                    patient={patient}
                    request={request}
                    serviceCode={request.service.code}
                    serviceId={request.serviceId}
                    serviceType={request.serviceType}
                />,
                <ConvertToReferralButton
                    acceptDiagnosticReport={acceptDiagnosticReport}
                    diagnosticReport={diagnosticReport}
                    isPreviousRequest={isPreviousRequest}
                    key="ConvertToReferralButton"
                    onConvertToReferral={onConvertToReferral}
                    patient={patient}
                    request={request}
                    serviceCode={request.service.code}
                    serviceId={request.serviceId}
                    serviceType={request.serviceType}
                />,
                <ConfirmDiagnosticReportButton
                    acceptDiagnosticReport={acceptDiagnosticReport}
                    diagnosticReport={diagnosticReport}
                    key="ConfirmDiagnosticReportButton"
                    patient={patient}
                    request={request}
                    serviceCode={request.service.code}
                />,
                <TakeSupervisionButton
                    key="TakeSupervisionButton"
                    practitionerId={practitionerId}
                    reloadProcess={reloadProcess}
                    supervision={supervision}
                    task={task}
                />,
                <UpdateSupervisionButton
                    key="UpdateSupervisionButton"
                    practitionerId={practitionerId}
                    task={task}
                    updateSupervision={updateSupervision}
                />,
            ]}
        />
    );
}
